import { symptomCategories, symptomLevels } from '@/mocks/questionnaire';
import type { Answers } from '@/pages/home/types';

export interface FocusItem {
  label: string;
  level: string;
  score: number;
}

export interface Evaluation {
  totalScore: number;
  ratedCount: number;
  severeCount: number;
  focus: FocusItem[];
  levelTitle: string;
  levelDesc: string;
  badgeClass: string;
  advice: string[];
}

const scoreOf = (level: string) =>
  symptomLevels.find((item) => item.label === level)?.score ?? 0;

export function evaluate(answers: Answers): Evaluation {
  const focus: FocusItem[] = [];
  let totalScore = 0;
  let ratedCount = 0;
  let severeCount = 0;

  symptomCategories.forEach((category) => {
    category.items.forEach((item) => {
      const value = typeof answers[item.name] === 'string' ? (answers[item.name] as string) : '';
      if (!value) return;
      const score = scoreOf(value);
      totalScore += score;
      ratedCount += 1;
      if (score >= 2) {
        focus.push({ label: item.label, level: value, score });
        if (score === 3) severeCount += 1;
      }
    });
  });

  focus.sort((a, b) => b.score - a.score);

  let levelTitle = '轻度波动';
  let levelDesc = '你的身体信号还比较温和,做好饮食和作息调理,状态会稳得住。';
  let badgeClass = 'bg-primary-100 text-primary-700 border-primary-200';
  let advice = [
    '每天固定时间睡,睡前 1 小时放下手机,卧室调凉一点',
    '豆浆、豆腐、亚麻籽这类植物雌激素可以每天来一点',
    '每周 3 次快走或八段锦,每次 30 分钟就够',
    '三个月后复查一次,看看变化',
  ];

  if (totalScore >= 23) {
    levelTitle = '偏重,建议尽早就诊';
    levelDesc = '症状已经比较明显地影响生活,靠自己硬扛会比较辛苦,建议尽快面诊。';
    badgeClass = 'bg-primary-500 text-background-50 border-primary-500';
    advice = [
      '先挂妇科内分泌或更年期专科门诊,把这份表给医生看',
      '明显影响睡眠和情绪时,可以咨询医生是否需要激素补充治疗(HRT)',
      '补钙 1000mg/天 + 维生素 D,预防骨量流失',
      '减少咖啡、浓茶、酒精,这些会让潮热更频繁',
    ];
  } else if (totalScore >= 13) {
    levelTitle = '中度不适';
    levelDesc = '几个症状已经在互相影响(比如睡不好→情绪差→更热),现在调整还来得及。';
    badgeClass = 'bg-accent-200 text-accent-900 border-accent-300';
    advice = [
      '先抓睡眠:固定起床时间,下午 3 点后不喝咖啡',
      '潮热发作时做 3 分钟深长呼吸,能明显缩短发作时间',
      '每天一份豆制品 + 一份深绿色蔬菜 + 一份高钙食物',
      '每周 2 次力量训练,保住肌肉和骨密度',
      '如果两个月后依旧影响生活,建议就医评估',
    ];
  } else if (totalScore >= 5) {
    levelTitle = '轻度波动';
    levelDesc = '身体在换挡期,有几个信号冒出来了,但还在可控范围。';
    advice = [
      '规律三餐,少糖少油,晚上别吃太饱',
      '每天走 6000 步,再配合拉伸放松肩颈',
      '睡前温水泡脚 15 分钟,帮助入睡',
      '情绪上来时先离开现场,喝口水再说话',
    ];
  }

  if (ratedCount === 0) {
    levelTitle = '还没开始评估';
    levelDesc = '上一页的症状自查还没有勾选,回去补一下就能看到结果啦。';
    badgeClass = 'bg-secondary-100 text-foreground-600 border-secondary-200';
    advice = ['返回「症状自查」,按最近两周的真实感受勾选即可'];
  }

  return {
    totalScore,
    ratedCount,
    severeCount,
    focus: focus.slice(0, 3),
    levelTitle,
    levelDesc,
    badgeClass,
    advice,
  };
}

export function menstrualLabel(answers: Answers): string {
  const value = typeof answers.menstrual === 'string' ? answers.menstrual : '';
  return value || '未填写';
}